import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return (
      /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
        ?.href || this.dataset.productUrl || this.closest('[data-product-url]')?.dataset?.productUrl
    );
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /**
   * Runs the native quick-add control (choose → modal, or add-to-cart submit).
   * Used by product card mobile shortcuts.
   * @param {Event} event
   */
  triggerPrimary = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const choose = this.querySelector('.quick-add__button--choose');
    const add = this.querySelector('.quick-add__button--add');

    if (choose instanceof HTMLButtonElement) {
      choose.click();
    } else if (add instanceof HTMLButtonElement) {
      add.click();
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    // Ensure quick-view summary order:
    // description (clamped) -> view details link -> stock tag (low/out of stock)
    // The markup for the full product form is fetched from the PDP and morphed in,
    // so we adjust the summary layout here for consistency.
    const detailsRoot = modalContent.querySelector('.product-details');
    if (!(detailsRoot instanceof HTMLElement)) return;

    const descriptionEl = /** @type {HTMLElement | null} */ (
      detailsRoot.querySelector('.text-block.rte:not(product-price)')
    );
    if (!descriptionEl) return;

    const productAnchor = /** @type {HTMLAnchorElement | null} */ (
      detailsRoot.querySelector('.view-product-title a')
    );
    const productHref = productAnchor?.href || this.productPageUrl;
    if (!productHref) return;

    let viewDetailsWrapper = /** @type {HTMLElement | null} */ (
      detailsRoot.querySelector('.view-more-details__wrapper')
    );

    if (!viewDetailsWrapper) {
      viewDetailsWrapper = document.createElement('div');
      viewDetailsWrapper.className = 'view-more-details__wrapper';

      const viewDetailsLink = document.createElement('a');
      viewDetailsLink.className = 'view-more-details link';
      viewDetailsLink.href = productHref;
      viewDetailsLink.textContent = 'View more details';

      viewDetailsWrapper.appendChild(viewDetailsLink);
    }

    // Insert wrapper right after the clamped description.
    if (viewDetailsWrapper.parentElement !== descriptionEl.parentElement || viewDetailsWrapper.previousElementSibling !== descriptionEl) {
      descriptionEl.insertAdjacentElement('afterend', viewDetailsWrapper);
    }

    const inventoryEl = /** @type {HTMLElement | null} */ (detailsRoot.querySelector('product-inventory'));
    if (inventoryEl) {
      // Place the stock tag just below the "view details" link.
      if (inventoryEl.previousElementSibling !== viewDetailsWrapper) {
        viewDetailsWrapper.insertAdjacentElement('afterend', inventoryEl);
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
